import React, { useState, useEffect } from "react";
import { X, ExternalLink } from "lucide-react";

// Mini visual previews for each layout — pure divs, no images.
const LayoutPreview = ({ id }) => {
  const base =
    "w-full h-full rounded-md overflow-hidden bg-gradient-to-br from-[#1a2552] to-[#0f1a3a] p-2 flex gap-1.5";
  const block = "rounded-sm bg-gradient-to-br from-purple-500/40 to-emerald-400/30";
  const muted = "rounded-sm bg-white/10";

  switch (id) {
    case "classic-saas":
      return (
        <div className={base}>
          <div className={`${block} flex-1`} />
          <div className={`${muted} w-1/3`} />
        </div>
      );
    case "centered-big-promise":
      return (
        <div className={`${base} flex-col items-center justify-center`}>
          <div className={`${block} w-full h-2/3`} />
          <div className={`${muted} w-2/3 h-1/4 mt-1`} />
        </div>
      );
    case "background-card":
      return (
        <div className={`${base} relative items-center justify-center`}>
          <div className={`${block} absolute inset-1`} />
          <div className={`${muted} relative w-2/3 h-2/3 z-10 bg-[#0a1a3a]/80`} />
        </div>
      );
    case "agenda-first":
      return (
        <div className={`${base} flex-col`}>
          <div className={`${block} w-full h-1/3`} />
          <div className="flex gap-1 flex-1">
            <div className={`${muted} flex-1`} />
            <div className={`${muted} flex-1`} />
            <div className={`${muted} flex-1`} />
          </div>
        </div>
      );
    case "evergreen-on-demand":
      return (
        <div className={`${base} flex-col`}>
          <div className={`${block} w-full h-1/2`} />
          <div className="flex gap-1 mt-1 flex-1">
            <div className={`${muted} flex-1`} />
            <div className={`${muted} flex-1`} />
          </div>
        </div>
      );
    default:
      return <div className={base} />;
  }
};

const LAYOUTS = [
  {
    id: "classic-saas",
    name: "Classic SaaS",
    description: "Two-column hero with form on the side",
  },
  {
    id: "centered-big-promise",
    name: "Centered Big Promise",
    description: "Centered hero with scrolling form",
  },
  {
    id: "background-card",
    name: "Background Card",
    description: "Full background with centered card",
  },
  {
    id: "agenda-first",
    name: "Agenda First",
    description: "Agenda and speakers prominently displayed",
  },
  {
    id: "evergreen-on-demand",
    name: "Evergreen On-Demand",
    description: "Watch now or time slot picker",
  },
];

const FORMATS = [
  { value: "live", label: "Live" },
  { value: "on-demand", label: "On-Demand" },
  { value: "hybrid", label: "Hybrid" },
];

const toDateInput = (d) => {
  if (!d) return "";
  try {
    const date = new Date(d);
    const tzOffset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
  } catch {
    return "";
  }
};

const EventLandingPageModal = ({
  isOpen,
  isEdit,
  initialData,
  onClose,
  onSubmit,
  readOnly = false,
}) => {
  const [form, setForm] = useState({
    yourName: "",
    description: "",
    layoutType: "classic-saas",
    format: "live",
    scheduledStartTime: "",
    heroHeadline: "",
    heroSubheadline: "",
    ctaButtonText: "Register Now",
    totalCapacity: 100,
    remainingSpots: 100,
    showCountdownTimer: true,
    showLimitedSeatsWarning: false,
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        yourName: initialData.yourName || "",
        description: initialData.description || "",
        layoutType: initialData.layoutType || "classic-saas",
        format: initialData.format || "live",
        scheduledStartTime: toDateInput(initialData.scheduledStartTime),
        heroHeadline: initialData.heroHeadline || "",
        heroSubheadline: initialData.heroSubheadline || "",
        ctaButtonText: initialData.ctaButtonText || "Register Now",
        totalCapacity: initialData.totalCapacity ?? 100,
        remainingSpots: initialData.remainingSpots ?? 100,
        showCountdownTimer: initialData.showCountdownTimer ?? true,
        showLimitedSeatsWarning: initialData.showLimitedSeatsWarning ?? false,
      });
    }
  }, [initialData]);

  if (!isOpen) return null;

  const update = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (readOnly) {
      onClose();
      return;
    }
    const payload = {
      ...form,
      totalCapacity: Number(form.totalCapacity) || 0,
      remainingSpots: Number(form.remainingSpots) || 0,
      scheduledStartTime: form.scheduledStartTime
        ? new Date(form.scheduledStartTime).toISOString()
        : null,
    };
    onSubmit(payload);
  };

  const inputCls =
    "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 disabled:opacity-60";
  const labelCls = "block text-sm font-medium text-gray-300 mb-1";
  const sectionCls = "text-base font-semibold text-white mb-3 mt-2";

  const title = readOnly
    ? "View Event"
    : isEdit
    ? "Edit Event"
    : "Create Event";

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="font-sans bg-[#0a1a3a] border border-white/10 rounded-2xl w-full max-w-3xl my-8 shadow-2xl shadow-purple-900/20 max-h-[calc(100vh-4rem)] overflow-y-auto">
        {/* Header */}
        <div className="px-7 pt-6 pb-0 border-b border-white/10">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{title}</h2>
              <p className="text-sm text-gray-400 mt-1">
                Configure your event landing page
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 text-gray-400"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          {/* Settings tab indicator */}
          <div className="mt-5 flex gap-6">
            <button
              type="button"
              className="relative pb-3 text-sm font-medium text-white"
            >
              Settings
              <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-full" />
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-7 space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className={sectionCls}>Basic Information</h3>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Event Name *</label>
                <input
                  type="text"
                  required
                  disabled={readOnly}
                  value={form.yourName}
                  onChange={(e) => update("yourName", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Description</label>
                <textarea
                  rows={2}
                  disabled={readOnly}
                  value={form.description}
                  onChange={(e) => update("description", e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          {/* Layout Type */}
          <div>
            <label className={labelCls}>Layout Type *</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {LAYOUTS.map((l) => {
                const selected = form.layoutType === l.id;
                return (
                  <button
                    type="button"
                    key={l.id}
                    disabled={readOnly}
                    onClick={() => update("layoutType", l.id)}
                    className={`relative text-left rounded-xl border overflow-hidden transition-all ${
                      selected
                        ? "border-purple-500 bg-purple-500/5 ring-1 ring-purple-500/40"
                        : "border-white/10 bg-white/5 hover:bg-white/10 hover:border-white/20"
                    }`}
                  >
                    {selected && (
                      <span className="absolute top-2 right-2 z-10 text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white flex items-center gap-1 font-medium shadow-lg">
                        <ExternalLink className="h-3 w-3" />
                        Demo
                      </span>
                    )}
                    {/* Preview */}
                    <div className="h-24 p-3 pb-0">
                      <LayoutPreview id={l.id} />
                    </div>
                    {/* Text */}
                    <div className="p-3">
                      <div className="text-sm font-semibold text-white">
                        {l.name}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        {l.description}
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Format & Date */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Format *</label>
              <select
                disabled={readOnly}
                value={form.format}
                onChange={(e) => update("format", e.target.value)}
                className={inputCls}
              >
                {FORMATS.map((f) => (
                  <option key={f.value} value={f.value} className="bg-[#0a1a3a]">
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Start Date &amp; Time *</label>
              <input
                type="datetime-local"
                disabled={readOnly}
                value={form.scheduledStartTime}
                onChange={(e) => update("scheduledStartTime", e.target.value)}
                className={inputCls}
              />
            </div>
          </div>

          {/* Hero Content */}
          <div>
            <h3 className={sectionCls}>Hero Content</h3>
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Headline</label>
                <input
                  type="text"
                  disabled={readOnly}
                  value={form.heroHeadline}
                  onChange={(e) => update("heroHeadline", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Subheadline</label>
                <textarea
                  rows={2}
                  disabled={readOnly}
                  value={form.heroSubheadline}
                  onChange={(e) => update("heroSubheadline", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>CTA Button Text</label>
                <input
                  type="text"
                  disabled={readOnly}
                  value={form.ctaButtonText}
                  onChange={(e) => update("ctaButtonText", e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
          </div>

          {/* Capacity & Urgency */}
          <div>
            <h3 className={sectionCls}>Capacity &amp; Urgency</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Total Capacity</label>
                <input
                  type="number"
                  min="0"
                  disabled={readOnly}
                  value={form.totalCapacity}
                  onChange={(e) => update("totalCapacity", e.target.value)}
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Remaining Spots</label>
                <input
                  type="number"
                  min="0"
                  disabled={readOnly}
                  value={form.remainingSpots}
                  onChange={(e) => update("remainingSpots", e.target.value)}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="flex items-center gap-6 mt-3">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={form.showCountdownTimer}
                  onChange={(e) => update("showCountdownTimer", e.target.checked)}
                />
                Show Countdown Timer
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  disabled={readOnly}
                  checked={form.showLimitedSeatsWarning}
                  onChange={(e) =>
                    update("showLimitedSeatsWarning", e.target.checked)
                  }
                />
                Show Limited Seats Warning
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10"
            >
              {readOnly ? "Close" : "Cancel"}
            </button>
            {!readOnly && (
              <button
                type="submit"
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white font-semibold hover:shadow-lg hover:shadow-purple-500/25"
              >
                {isEdit ? "Save Changes" : "Create Event"}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default EventLandingPageModal;
